import { useEffect, useRef, useState } from "react";
import { DataService } from "@/services/data";
import { CHECK_FORMULA_URL, FieldFormula, FormulaCheckResult } from "@/types/formula";
import { useDebounce } from "@/utils/misc";
import { useFormulaScope } from "@/providers/formula-scope";

/** How long a keystroke waits before it is worth a request. */
export const CHECK_DEBOUNCE_MS = 350;

export type FormulaCheckState = {
    /** Diagnostics of the last source the server answered about, or `null` if none was. */
    result: FormulaCheckResult | null;
    /** The source those diagnostics describe. Never assume it is the current one. */
    checked: string;
    /** The source has moved on and the answer for it has not arrived. */
    checking: boolean;
    /** The endpoint could not be reached, so silence here means nothing. */
    unavailable: boolean;
};

/**
 * Diagnostics for a source, refreshed while its author types.
 *
 * Three properties, and each of them is why this is a hook rather than a `fetch` in a
 * component:
 *
 * 1. **debounced**, so a word typed at speed costs one request and not eight. The endpoint
 *    reads no record and evaluates nothing, which is what makes it safe to call on a
 *    keystroke at all, but it still parses and still crosses the network.
 * 2. **one at a time.** A check in flight is never abandoned and never joined: the newest
 *    source is remembered and sent when the answer comes back. Two checks racing would let
 *    the older answer land last and underline characters the author has already fixed.
 * 3. **honest about what it describes.** `checked` is the source the diagnostics are about,
 *    so a caller marks the underlines as stale rather than moving them onto text they were
 *    not computed for — the same discipline as the autosave badge, which must not read
 *    « Saved » while anything is unconfirmed.
 *
 * The declarations of the document being edited (`FormulaScopeProvider`) travel with every
 * check, so `variables.typeform_id` is known the moment it is declared — before the draft
 * has saved it — and a change of declarations re-checks the source on screen.
 *
 * An empty source is not checked. « This field is empty » is the form's business, and the
 * drawer deliberately keeps incomplete values: a blank field must not fill the panel with
 * a diagnostic about nothing.
 */
export function useFormulaCheck(source: string, formula: FieldFormula | null | undefined, { delay = CHECK_DEBOUNCE_MS }: { delay?: number } = {}): FormulaCheckState {
    const debounced = useDebounce(source, delay);
    const [state, setState] = useState<{ result: FormulaCheckResult | null; checked: string; unavailable: boolean }>({ result: null, checked: "", unavailable: false });

    const enabled = Boolean(formula);
    const mode = formula?.mode ?? "formula";
    const expects = formula?.expects ?? "any";
    const { declarations } = useFormulaScope();
    const declared = JSON.stringify(declarations);

    const alive = useRef(true);
    const inFlight = useRef(false);
    /** Newest source worth checking, so the reply of the current check knows what to do next. */
    const wanted = useRef<string | null>(null);

    useEffect(() => {
        alive.current = true;
        return () => {
            alive.current = false;
        };
    }, []);

    useEffect(() => {
        if (!enabled) return;

        const check = (candidate: string) => {
            if (!candidate.trim()) {
                setState({ result: null, checked: candidate, unavailable: false });
                return;
            }
            if (inFlight.current) return;
            inFlight.current = true;
            DataService.client
                .url(CHECK_FORMULA_URL)
                .post(declarations.length > 0 ? { source: candidate, mode, expects, variables: declarations } : { source: candidate, mode, expects })
                .json<FormulaCheckResult>()
                .then(result => {
                    if (alive.current) setState({ result, checked: candidate, unavailable: false });
                })
                .catch(() => {
                    // A refused or unreachable check is reported and never turned into « no
                    // problem found »: publication checks the same formula server-side, and
                    // an editor that silently claimed a source was fine would be lying about
                    // what it knows.
                    if (alive.current) setState({ result: null, checked: candidate, unavailable: true });
                })
                .finally(() => {
                    inFlight.current = false;
                    const queued = wanted.current;
                    if (alive.current && queued !== null && queued !== candidate) check(queued);
                });
        };

        wanted.current = debounced;
        check(debounced);
        // `declared` stands for `declarations`: compared by content, so a projection echoed
        // back unchanged by an autosave does not cost a request.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debounced, mode, expects, enabled, declared]);

    return { ...state, checking: enabled && source !== state.checked };
}
